import buildPositionKey from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/buildPositionKey';
import ViewerFieldIndex, {
	type FieldPlacement,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/ViewerFieldIndex';
import type DocumentDetailView from '@UI/Store/Documents/Views/DocumentDetailView';
import type { DocumentEntryView } from '@UI/Store/Documents/Views/buildDocumentEntryView';
import type DocumentListEntryView from '@UI/Store/Documents/Views/DocumentListEntryView';

type Accumulator = {
	readonly fieldKeys: Map<string, string>;
	readonly lists: Map<string, DocumentListEntryView>;
	readonly placements: Map<string, FieldPlacement>;
	readonly listPlacements: Map<string, FieldPlacement>;
};

const collectEntries = (
	entries: readonly DocumentEntryView[],
	positions: readonly number[],
	accumulator: Accumulator,
): void => {
	for (const entry of entries) {
		if (entry.type === 'field') {
			accumulator.fieldKeys.set(buildPositionKey(entry.uuid, positions), entry.key);
			accumulator.placements.set(entry.key, { uuid: entry.uuid, positions });
			continue;
		}

		if (entry.type === 'group') {
			collectEntries(entry.entries, positions, accumulator);
			continue;
		}

		accumulator.lists.set(buildPositionKey(entry.uuid, positions), entry);
		accumulator.listPlacements.set(entry.key, { uuid: entry.uuid, positions });

		for (const item of entry.items) {
			collectEntries(item.entries, [...positions, item.index], accumulator);
		}
	}
};

/** Walks the document tree once, noting for every field and list the chain of list positions above it. */
const buildViewerFieldIndex = (document: DocumentDetailView): ViewerFieldIndex => {
	const accumulator: Accumulator = {
		fieldKeys: new Map(),
		lists: new Map(),
		placements: new Map(),
		listPlacements: new Map(),
	};

	for (const section of document.sections) {
		collectEntries(section.entries, [], accumulator);
	}

	return new ViewerFieldIndex(
		accumulator.fieldKeys,
		accumulator.lists,
		accumulator.placements,
		accumulator.listPlacements,
	);
};

export default buildViewerFieldIndex;
